import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./VehicleCard.css";

const VehicleCard = ({ vehicle }) => {
  const [mediaList, setMediaList] = useState([]);
  const [colorImageMap, setColorImageMap] = useState({});
  const [availableColors, setAvailableColors] = useState([]);
  const [activeColorIndex, setActiveColorIndex] = useState(0);
  const [currentImage, setCurrentImage] = useState(null);
  const [manualSelected, setManualSelected] = useState(false);
  const navigate = useNavigate();

  // Fetch media on mount
  useEffect(() => {
    if (!vehicle?.vehicle_id) return;

    fetch(`${process.env.REACT_APP_API_URL || (window.location.hostname !== "localhost" ? "/api" : "http://localhost:5000/api")}/vehicles/${vehicle.vehicle_id}/media`)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data.media || [];
        setMediaList(list.filter((m) => m.media_type === "image"));
      })
      .catch(() => {
        setMediaList([]);
      });
  }, [vehicle?.vehicle_id]);

  // Build a map: color_id -> best matching image URL and filter colors
  useEffect(() => {
    if (!vehicle?.colors?.length || !mediaList.length) {
      setColorImageMap({});
      setAvailableColors([]);
      return;
    }

    const map = {};
    const filteredColors = [];

    vehicle.colors.forEach((color) => {
      const nameLower = (color.color_name_vi || "").toLowerCase();
      const enNameLower = (color.color_name_en || "").toLowerCase();
      const codeLower = (color.color_code || "").toLowerCase();

      let match = mediaList.find((m) => {
        const desc = (
          m.description ||
          m.color_name ||
          m.color_name_vi ||
          m.color_code ||
          m.label ||
          ""
        ).toLowerCase();
        const url = (m.media_url || m.url || "").toLowerCase();

        const labelMatch =
          desc &&
          (desc === nameLower ||
            (enNameLower && desc === enNameLower) ||
            (codeLower && desc === codeLower));
        const urlMatch =
          url &&
          (url.includes(nameLower) ||
            (enNameLower && url.includes(enNameLower)) ||
            (codeLower && url.includes(codeLower)));
        return labelMatch || urlMatch;
      });

      if (!match) {
        // Try loose matching
        match = mediaList.find((m) => {
          const desc = (
            m.description ||
            m.color_name ||
            m.color_name_vi ||
            m.color_code ||
            m.label ||
            ""
          ).toLowerCase();
          const url = (m.media_url || m.url || "").toLowerCase();
          return (
            (nameLower &&
              (desc.includes(nameLower) || url.includes(nameLower))) ||
            (codeLower && (desc.includes(codeLower) || url.includes(codeLower)))
          );
        });
      }

      if (match) {
        map[color.color_id] = match.media_url || match.url || null;
        filteredColors.push(color);
      }
    });

    setColorImageMap(map);
    setAvailableColors(filteredColors);
    setActiveColorIndex(0); // reset index when colors change
  }, [mediaList, vehicle?.colors]);

  // Auto-rotate through available colors randomly (stop if user manually selected)
  useEffect(() => {
    if (availableColors.length <= 1 || manualSelected) return;

    let timeoutId;

    const changeColor = () => {
      setActiveColorIndex((prev) => {
        let next;
        do {
          next = Math.floor(Math.random() * availableColors.length);
        } while (next === prev);
        return next;
      });
      // Randomize the next interval between 2s and 3.5s
      timeoutId = setTimeout(changeColor, 2000 + Math.random() * 1500);
    };

    // Randomize initial start delay between 0.5s and 2s
    timeoutId = setTimeout(changeColor, 500 + Math.random() * 1500);

    return () => clearTimeout(timeoutId);
  }, [availableColors.length, manualSelected]);

  const handleColorClick = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveColorIndex(idx);
    setManualSelected(true);
  };

  const handleCardClick = () => {
    navigate(`/vehicle/${vehicle.vehicle_id}`);
  };

  // Update displayed image whenever activeColorIndex or map changes
  useEffect(() => {
    const activeColor = availableColors[activeColorIndex];
    if (activeColor) {
      setCurrentImage(colorImageMap[activeColor.color_id] || null);
    } else {
      setCurrentImage(null);
    }
  }, [activeColorIndex, availableColors, colorImageMap]);

  const formatPrice = (price) => {
    if (!price) return "Liên hệ";
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const activeColor = availableColors[activeColorIndex];

  return (
    <div
      className="vehicle-card-link"
      onClick={handleCardClick}
      style={{ cursor: "pointer" }}
    >
      <div className="vehicle-card">
        <div className="vehicle-card-image">
          {currentImage ?
            <img
              src={currentImage}
              alt={activeColor?.color_name_vi || vehicle.vehicle_name}
              className="vehicle-img"
            />
          : <div className="vehicle-placeholder">
              <span className="vehicle-type-badge">{vehicle.vehicle_type}</span>
            </div>
          }
          {vehicle.segment && (
            <span className="vehicle-segment-badge">{vehicle.segment}</span>
          )}
        </div>

        <div className="vehicle-card-content">
          <h3 className="vehicle-name">{vehicle.vehicle_name}</h3>

          <p className="vehicle-meta">
            <span className="vehicle-type-label">{vehicle.vehicle_type}</span>
            {vehicle.segment && (
              <>
                <span className="meta-separator"> · </span>
                <span className="vehicle-segment-label">{vehicle.segment}</span>
              </>
            )}
          </p>

          {availableColors.length > 0 && (
            <div className="vehicle-colors">
              {availableColors.map((color, idx) => (
                <div
                  key={color.color_id}
                  className={`color-dot${idx === activeColorIndex ? " color-dot--active" : ""}`}
                  style={{
                    background:
                      color.is_vip === 1 ?
                        `linear-gradient(135deg, ${color.color_hex_code || "#ccc"} 50%, ${color.color_hex_code_2 || color.color_hex_code || "#ccc"} 50%)`
                      : color.color_hex_code || "#ccc",
                  }}
                  title={
                    color.is_vip === 1 ?
                      `⭐ ${color.color_name_vi}`
                    : color.color_name_vi
                  }
                  onClick={(e) => handleColorClick(e, idx)}
                />
              ))}
            </div>
          )}

          <div className="vehicle-price">
            <span className="price-label">Giá từ:</span>
            <span className="price-value">
              {formatPrice(vehicle.final_price)}
            </span>
          </div>

          <button className="btn btn-primary btn-small">Xem Chi Tiết</button>
        </div>
      </div>
    </div>
  );
};

export default VehicleCard;
